<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Skills editor (D-protocol P1a): the brain's skills[] rulebooks, edited inline
// on their own tab. Each entry is { name, content }. They live in the same
// implicit draft as the prompt blocks, so save() spreads the existing draft and
// overwrites only skills — every other draft field survives. Optimistic locking
// like the brain editor: a 409 means someone else edited; offer reload.
const { t } = useI18n();
const store = useStore();
const { brain, fetchBrain } = useBrain();

const uiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const isSaving = computed(() => uiFlags.value.updatingItem);

const skills = ref([]);
const hasConflict = ref(false);
const isHydrated = ref(false);

const hydrateFromDraft = () => {
  const draft = brain.value.draft_config || {};
  skills.value = (draft.skills || []).map(skill => ({
    name: skill.name || '',
    content: skill.content || '',
  }));
  isHydrated.value = true;
};

watch(
  () => brain.value.id,
  id => {
    if (id && !isHydrated.value) hydrateFromDraft();
  },
  { immediate: true }
);

const addSkill = () => {
  skills.value.push({ name: '', content: '' });
};

const removeSkill = index => {
  skills.value.splice(index, 1);
};

const moveSkill = (index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= skills.value.length) return;
  const next = skills.value.slice();
  [next[index], next[target]] = [next[target], next[index]];
  skills.value = next;
};

const save = async () => {
  try {
    await store.dispatch('aiAgentBrains/update', {
      id: brain.value.id,
      draftConfig: {
        ...(brain.value.draft_config || {}),
        skills: skills.value.map(skill => ({
          name: skill.name,
          content: skill.content,
        })),
      },
      lockVersion: brain.value.lock_version,
    });
    hasConflict.value = false;
    useAlert(t('AI_AGENTS.SKILLS.SAVED'));
  } catch (error) {
    if (error.response?.status === 409) {
      hasConflict.value = true;
    } else {
      useAlert(t('AI_AGENTS.SKILLS.ERROR'));
    }
  }
};

const reloadDraft = async () => {
  await fetchBrain();
  hydrateFromDraft();
  hasConflict.value = false;
};
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4 max-w-3xl">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h2 class="text-lg font-medium text-n-slate-12">
            {{ t('AI_AGENTS.SKILLS.HEADER') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ t('AI_AGENTS.SKILLS.DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('AI_AGENTS.SKILLS.ADD')"
          variant="ghost"
          size="sm"
          icon="i-lucide-plus"
          @click="addSkill"
        />
      </div>

      <div
        v-if="hasConflict"
        class="flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{ t('AI_AGENTS.AGENT.CONFLICT.MESSAGE') }}
        <Button
          :label="t('AI_AGENTS.AGENT.CONFLICT.RELOAD')"
          color="amber"
          size="sm"
          @click="reloadDraft"
        />
      </div>

      <p v-if="!skills.length" class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.SKILLS.EMPTY') }}
      </p>

      <div
        v-for="(skill, index) in skills"
        :key="index"
        class="flex flex-col gap-2 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2">
          <input
            v-model="skill.name"
            type="text"
            :placeholder="t('AI_AGENTS.SKILLS.NAME_PLACEHOLDER')"
            class="flex-1 px-3 py-2 text-sm font-medium rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
          />
          <Button
            :title="t('AI_AGENTS.SKILLS.MOVE_UP')"
            variant="ghost"
            size="sm"
            icon="i-lucide-arrow-up"
            :disabled="index === 0"
            @click="moveSkill(index, -1)"
          />
          <Button
            :title="t('AI_AGENTS.SKILLS.MOVE_DOWN')"
            variant="ghost"
            size="sm"
            icon="i-lucide-arrow-down"
            :disabled="index === skills.length - 1"
            @click="moveSkill(index, 1)"
          />
          <Button
            :title="t('AI_AGENTS.SKILLS.REMOVE')"
            variant="ghost"
            color="ruby"
            size="sm"
            icon="i-lucide-trash"
            @click="removeSkill(index)"
          />
        </div>
        <textarea
          v-model="skill.content"
          rows="6"
          :placeholder="t('AI_AGENTS.SKILLS.CONTENT_PLACEHOLDER')"
          class="px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12"
        />
      </div>

      <div class="flex justify-end">
        <Button
          :label="t('AI_AGENTS.SKILLS.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
